import { Suspense } from 'react';
import { createBrowserRouter } from 'react-router';
import App from './App';
import { JobsListLayout } from './components/JobsListLayout';
import { ConstrainedLayout } from './components/ConstrainedLayout';
import { JobDrawer } from './components/JobDrawer';
import { NotFound } from './components/NotFound';
import { RouteError } from './components/RouteError';
import { AdminJobPage } from './pages/AdminJobPage';
import { AdminPage } from './pages/AdminPage';
import { AdminRunPage } from './pages/AdminRunPage';
import { AdminUserPage } from './pages/AdminUserPage';
import { MePage } from './pages/MePage';
import { RunPage } from './pages/RunPage.lazy';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    // Last-resort boundary: render/loader errors that bubble past every child land
    // here instead of react-router's default unstyled error page. (This replaces the
    // whole shell, header included — the per-route boundaries below keep the header.)
    errorElement: <RouteError />,
    children: [
      // Full-bleed: the run screen uses the whole viewport width so the transcript
      // and journal terminal have room. It's a sibling of ConstrainedLayout, so it
      // isn't capped at max-w-6xl like the rest.
      {
        path: 'jobs/:jobId/runs/:runId',
        // RunPage (and JournalTerminal inside it) is lazy: a chunk-fetch rejection —
        // e.g. a user holding pre-deploy index.html after the deploy's --delete pass —
        // bypasses Suspense and surfaces here. Scoped to the route so the header stays.
        errorElement: <RouteError />,
        element: (
          <Suspense fallback={<div className="p-4 text-slate-500">Loading…</div>}>
            <RunPage />
          </Suspense>
        ),
      },
      {
        element: <ConstrainedLayout />,
        // Keeps the header when a list/drawer/me/admin screen throws.
        errorElement: <RouteError />,
        children: [
          {
            // Pathless layout route: <JobsListLayout/> renders <JobsTable/>
            // unconditionally and includes its own <Outlet/> for the drawer.
            element: <JobsListLayout />,
            children: [
              // Index renders nothing of its own — JobsListLayout's <JobsTable/>
              // is the visible content. The drawer overlays via the layout's
              // <Outlet/> when /jobs/:id matches.
              { index: true, element: null },
              { path: 'jobs/:id', element: <JobDrawer /> },
            ],
          },
          { path: 'me', element: <MePage /> },
          { path: 'admin', element: <AdminPage /> },
          { path: 'admin/users/:userId', element: <AdminUserPage /> },
          { path: 'admin/jobs/:jobId', element: <AdminJobPage /> },
          { path: 'admin/runs/:runId', element: <AdminRunPage /> },
          // Catch-all: CloudFront rewrites 403/404 → index.html, so an unknown deep
          // link loads the app and lands here instead of react-router's default
          // unstyled error page. Splat ranks last, so it only matches truly unknown URLs.
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);
